import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import Picker from 'emoji-picker-react';
import IconButton from '@material-ui/core/IconButton';
import AccountCircleOutlined from '@material-ui/icons/AccountCircleOutlined';
import InsertEmoticonOutlined from '@material-ui/icons/InsertEmoticonOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import _ from 'lodash';

import Poll from '../Poll/Poll';
import QuestionPanel from '../Q&A/QuestionPanel';
import ChatLoginOverlay from '../ChatOverlay/ChatLoginOverlay';
import { DownIcon } from '../QPlayerSVGElement';

import {
	ChatContainer,
	ChatTab,
	ChatTabWrapper,
	EmojiIconContainer,
	Message,
	MessageInput,
	MessageInputContainer,
	MessagesContainer,
	StartOfChat,
	MessageNotification,
	MessageNotificationContent,
	ChatTabAction,
	ChatNotice,
} from './ChatUpdated.styled';

const ChatUpdated = ({
	closeIncomingMessage,
	showChatWindow,
	t,
	messages: messagesProps,
	questions,
	onChatInputFocus,
	onKeyUp,
	qna,
	poll,
	pollResult,
	messageContainerRef: messageContainerRefProps,
	settings,
	onChatNotificationClick,
	onEditProfileClick,
	disabledChatInput,
	onRefreshButtonClick,
	onUpvoteButtonClick,
	onSendQuestionClick,
	onFocusOnQuestionField,
}) => {
	const [messageText, setMessageText] = useState('');
	const [showEmojiPicker, setShowEmojiPicker] = useState(false);
	const [showMessageNotification, setShowMessageNotification] = useState(false);
	const [isAllowToRefresh, setAllowToRefresh] = useState(true);

	const chatInputRef = useRef(null);

	const { username } = useSelector((state) => state.chatLoginReducer);
	const { accountId, isChatActivated } = useSelector((state) => state.configurationReducer);

	const messToShow = useMemo(() => {
		const messages = isChatActivated ? messagesProps ?? [] : [];
		let mess = [...(messages ?? []), ...(questions ?? [])];
		mess = mess.sort((messA, messB) => {
			return new Date(messA.created) - new Date(messB.created);
		});
		return mess;
	}, [messagesProps, questions, isChatActivated]);

	const pinnedMessage = useMemo(() => {
		const pinnedMessages = messagesProps?.filter((item) => item.message.pinned);
		return (pinnedMessages ?? []).pop();
	}, [messagesProps]);

	const messageContainerRef = useRef(null);

	useEffect(() => {
		if (!showMessageNotification) {
			return;
		}

		const showNotification =
			Math.abs(messageContainerRef.current.scrollHeight - messageContainerRef.current.scrollTop - messageContainerRef.current.clientHeight) >
			100;
		if (showNotification !== showMessageNotification) {
			setShowMessageNotification(showNotification);
		}
	}, [showMessageNotification]);

	useEffect(() => {
		if (!messageContainerRef || !messageContainerRef.current || (messToShow ?? []).length === 0) {
			setShowMessageNotification(false);
			return;
		}

		setShowMessageNotification(
			Math.abs(messageContainerRef.current.scrollHeight - messageContainerRef.current.scrollTop - messageContainerRef.current.clientHeight) >
				100
		);
	}, [messageContainerRef, messToShow]);

	useEffect(() => {
		if (closeIncomingMessage || disabledChatInput || !chatInputRef?.current) {
			return;
		}
		chatInputRef?.current?.focus();
	}, [disabledChatInput, chatInputRef, closeIncomingMessage]);

	const onNotificationClick = useCallback(() => {
		onChatNotificationClick?.();
		setShowMessageNotification(false);
	}, [onChatNotificationClick]);

	const onRefreshClick = useCallback(() => {
		if (!isAllowToRefresh || closeIncomingMessage) {
			return;
		}

		setAllowToRefresh(false);
		onRefreshButtonClick();
		const allowToRefreshTimeout = setTimeout(() => {
			clearTimeout(allowToRefreshTimeout);
			setAllowToRefresh(true);
		}, 3000);
	}, [closeIncomingMessage, isAllowToRefresh, onRefreshButtonClick]);

	const isVoted = useCallback(
		(id) => {
			const upvotedQuestions = Cookies.get(`chat_upvoted_${accountId}`);
			return (upvotedQuestions ?? '').includes(id);
		},
		[accountId]
	);

	return (
		<ChatContainer showChatWindow={showChatWindow} className="chat-container">
			<ChatTabWrapper>
				<ChatTab>{t(isChatActivated ? 'CHAT' : 'QNA')}</ChatTab>
				<ChatTabAction>
					<IconButton onClick={onEditProfileClick} title={t('PROFILE_SETTINGS')}>
						<AccountCircleOutlined htmlColor="#000" />
					</IconButton>
				</ChatTabAction>
			</ChatTabWrapper>

			<MessagesContainer
				ref={(ref) => {
					if (messageContainerRefProps) {
						messageContainerRefProps.current = ref;
					}
					messageContainerRef.current = ref;
				}}
				style={isChatActivated ? undefined : { padding: '12px 12px 8px 12px' }}
			>
				{isChatActivated && <StartOfChat style={poll || qna ? { marginTop: '80px' } : undefined}>{t('START_OF_CHAT')}</StartOfChat>}
				{[...(messToShow ?? {})].map(({ id, sender: { admin, name }, recipient, question, message, blocked, votes, reply, qnaId }, i) => {
					const replyTo =
						recipient?.replyMessageId || recipient?.replyQuestionId
							? messToShow.find((m) => m.id === recipient?.replyMessageId || m.id === recipient?.replyQuestionId)
							: undefined;

					return (
						!blocked && (
							<Message
								primaryColor={settings?.playerStyling?.primaryColor ?? '#126cfc'}
								key={id + i}
								id={id}
								isAdmin={admin}
								name={name}
								content={qnaId ? (replyTo ? reply : question) : message?.text}
								replyContent={replyTo?.qnaId ? replyTo?.question : replyTo?.message?.text}
								isQuestion={!!(question && !reply)}
								onUpvoteButtonClick={(e) => onUpvoteButtonClick?.(e, id, qnaId)}
								votes={votes ?? 0}
								isVoted={isVoted(id)}
							/>
						)
					);
				})}
			</MessagesContainer>
			{showEmojiPicker && isChatActivated && (
				<Picker
					pickerStyle={{
						position: 'absolute',
						bottom: '64px',
						left: '0',
						width: '100%',
					}}
					onEmojiClick={(_e, emojiObject) => {
						setMessageText((prev) => prev + emojiObject.emoji);
						setShowEmojiPicker(false);
					}}
					disableSearchBar={true}
					disableAutoFocus={true}
					disableSkinTonePicker={true}
					preload={true}
				/>
			)}

			{isChatActivated && (
				<Message
					primaryColor={settings?.playerStyling?.primaryColor ?? '#126cfc'}
					name={pinnedMessage?.sender.name}
					content={pinnedMessage?.message?.text}
					replyContent={
						pinnedMessage?.recipient.replyMessageId &&
						messagesProps.find((x) => x.id === pinnedMessage?.recipient?.replyMessageId)?.message?.text
					}
					isPinned={true}
					isAdmin={pinnedMessage?.sender.admin}
					notificationComponent={() =>
						showMessageNotification && (
							<MessageNotification>
								<MessageNotificationContent onClick={onNotificationClick}>
									New message <DownIcon />
								</MessageNotificationContent>
							</MessageNotification>
						)
					}
				/>
			)}
			{isChatActivated && (
				<div>
					<ChatNotice>{messageText.trim().length}/300 characters</ChatNotice>
					<MessageInputContainer>
						<MessageInput
							ref={chatInputRef}
							tabIndex={0}
							placeholder={username ? `${t('SEND_MESSAGE_AS')}${username}` : t('WRITE_MESSAGE')}
							contentEditable="plaintext-only"
							onClick={onChatInputFocus}
							onKeyUp={(e) => {
								onKeyUp(e, messageText, setMessageText);
							}}
							value={messageText}
							onInput={(e) => {
								onChatInputFocus?.();

								const value = e.target.value;
								if (value.trim().length > 300) {
									return;
								}
								setMessageText(value);
							}}
							disabled={disabledChatInput || closeIncomingMessage}
						/>
						<EmojiIconContainer
							closeIncomingMessage={closeIncomingMessage}
							onClick={() => !closeIncomingMessage && setShowEmojiPicker(!showEmojiPicker)}
						>
							<InsertEmoticonOutlined style={{ width: '18px', height: '18px' }} />
						</EmojiIconContainer>

						<IconButton
							onClick={onRefreshClick}
							disabled={!isAllowToRefresh || closeIncomingMessage}
							style={{
								position: 'absolute',
								right: '0px',
								zIndex: 1,
							}}
							title={t('REFRESH_CHAT')}
						>
							<RefreshIcon
								htmlColor={isAllowToRefresh && !closeIncomingMessage ? '#000' : undefined}
								style={{ width: '18px', height: '18px' }}
							/>
						</IconButton>
					</MessageInputContainer>
				</div>
			)}

			{!closeIncomingMessage && <Poll poll={poll} pollResult={pollResult} settings={settings} />}
			{!closeIncomingMessage &&
				(isChatActivated ? (
					<QuestionPanel
						t={t}
						qna={qna}
						onSendQuestionClick={onSendQuestionClick}
						onFocusOnQuestionField={onFocusOnQuestionField}
						settings={settings}
					/>
				) : (
					<div style={{ backgroundColor: 'rgba(247, 248, 250, 1)', border: '1px solid rgba(229, 233, 236, 1)' }}>
						<QuestionPanel
							t={t}
							qna={qna}
							onSendQuestionClick={onSendQuestionClick}
							onFocusOnQuestionField={onFocusOnQuestionField}
							settings={settings}
							noShadow
						/>
					</div>
				))}

			<ChatLoginOverlay settings={settings} />
		</ChatContainer>
	);
};

export default compose(withTranslation())(ChatUpdated);
